import React, { useEffect } from 'react';
import {
  matchPath,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import MainLayout from 'pages/authenticated/layout/mainLayout/MainLayout';
import Login from 'pages/authentication/Login/Login';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { settingsEndpoints, userEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import { useBrandingContext } from 'app-context/BrandingsContext';
import { useUserContext } from 'app-context/user/UserContext';

import { useTour } from 'app-context/tourContext/TourContext';

import { useSidebarData } from 'app-context/sidebar/SidebarDataProvider';
import {
  AGENT_ROUTES,
  BOT_SETTINGS_ROUTES,
  CONSOLE_SETTINGS_ROUTES,
  NONE_LAYOUT_BOT_ROUTES,
  ROUTES_MAP,
} from './data/Routes';
import Loader from 'component/atom/loader/Loader';
import getFallbackRoute from './factory/GetFallbackRoute';

const loaderContainer = {
  width: '100%',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const MainRouter = () => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;
  const { sidebarData, refetchSidebarData, isFetching } = useSidebarData();

  const navigationType = useNavigationType();
  const { setBrandingData } = useBrandingContext();
  const { setUserData } = useUserContext();
  const { stopTour } = useTour();
  // const loaderStyles = {
  //   width: '100%',
  //   height: '100vh',
  //   display: 'flex',
  //   justifyContent: 'center',
  //   alignItems: 'center',
  // };
  // call the branding endpoint to get the branding data
  const { data: brandingData, refetch } = useFetch(
    ['branding'],
    settingsEndpoints.getBranding(),
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (navigationType === 'POP') {
      stopTour();
    }
  }, [location.key]);

  // call the user endpoint to get the user data
  const { data: userData } = useFetch(['user'], userEndpoints.getAuthUser(), {
    enabled: true,
  });

  // set the branding data in the context
  useEffect(() => {
    if (brandingData?.data) {
      setBrandingData(brandingData.data);
    }
  }, [brandingData, setBrandingData]);

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  // set the user data in the context
  useEffect(() => {
    if (userData) {
      setUserData(userData?.data);
    }
  }, [userData, setBrandingData]);

  if (isAuthenticated && isFetching) {
    return (
      <div style={loaderContainer}>
        <Loader />
      </div>
    );
  }
  const groupedRoutes = { main: [], settings: [], console: [], none: [] };

  let validRoutes = [];
  const STATIC_EXTRA_ROUTES = [
    '/knowledge-base/:id',
    '/user/:id',
    '/profile',
    '/docs',
    '/edit-user/:id',
    '/groups',
    '/new-group',
    '/new-user',
    '/edit-group/:id',
    '/bot/contact-profile',
    '/bot/script/:id',
  ];

  if (isAuthenticated && Array.isArray(sidebarData?.data)) {
    const apiRoutes = sidebarData.data;
    const hasBots = apiRoutes.some((r) => r.routeTo === '/bots');
    const hasConsole = apiRoutes.some((r) => r.routeTo === '/chat-console');

    const allRoutes = [
      ...apiRoutes,
      ...(hasBots
        ? BOT_SETTINGS_ROUTES.map((path) => ({ routeTo: path }))
        : []),
      ...(hasBots
        ? NONE_LAYOUT_BOT_ROUTES.map((path) => ({ routeTo: path }))
        : []),
      ...(hasBots ? AGENT_ROUTES.map((path) => ({ routeTo: path })) : []),
      ...(hasConsole
        ? CONSOLE_SETTINGS_ROUTES.map((path) => ({ routeTo: path }))
        : []),
      ...STATIC_EXTRA_ROUTES.map((path) => ({ routeTo: path })),
    ];
    validRoutes = allRoutes
      .filter(({ routeTo }) => ROUTES_MAP[routeTo])
      .sort((a, b) => a.routeOrder - b.routeOrder || 0);
  }
  validRoutes.forEach(({ routeTo }) => {
    const matchedKey = Object.keys(ROUTES_MAP).find((pattern) =>
      matchPath(pattern, routeTo)
    );

    const routeConfig = ROUTES_MAP[matchedKey];
    if (routeConfig) {
      const layout = routeConfig.layout || 'main';
      groupedRoutes[layout].push(
        <Route key={routeTo} path={routeTo} element={routeConfig.element} />
      );
    }
  });

  return (
    <Routes>
      <Route path='/' element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          {groupedRoutes.main}
          {groupedRoutes.settings}
          {groupedRoutes.console}
        </Route>
        {groupedRoutes.none}
      </Route>
      <Route path='/' element={<PublicRoute />}>
        <Route index element={<Login />} />
      </Route>
      <Route path='*' element={getFallbackRoute()} />
    </Routes>
  );
};

export default MainRouter;
